import { Button } from "@/components/ui/baseUi/button"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/baseUi/combobox"
import { Input } from "@/components/ui/baseUi/input"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { CoachActivityTypeEnum, getCoachActivitiesSortedByLabel } from "@/modules/coach/coachActivityTypes"
import { CoachBannerCard } from "@/modules/coach/components/coachBannerCard"
import { CoachSectionCard } from "@/modules/coach/components/coachSectionCard"
import { useCurrentCoach, useUpdateCoachSettings } from "@/modules/coach/hooks/useProfessionalCoach"
import { coachFormDataSchema, getCoachFormDataDefaultValues } from "@/modules/coach/types/coachFormTypes"
import { useLanguage } from "@/modules/language/hooks/useLanguage"
import { useUser } from "@/modules/users/hooks/useUser"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { word } from "@modules/core/utils/i18n"
import { Plus, PlusIcon } from "lucide-react"
import { useMemo, useState } from "react"

const ROLE_MAX_LENGTH = 50
const CITY_MAX_LENGTH = 80
const REGION_MAX_LENGTH = 80
const DESCRIPTION_MAX_LENGTH = 5000
const FREE_ACTIVITY_MAX_LENGTH = 25
const MAX_ACTIVITIES = 5

type Props = {
  formId: string
}

export function CoachAdminForm({ formId }: Props) {
  const { currentCoach: coach } = useCurrentCoach()
  const { mutateAsync: updateCoachSettings } = useUpdateCoachSettings()
  const locale = useLanguage()
  const chipsAnchor = useComboboxAnchor()
  const user = useUser()

  const coachActivitiesSortedByLabel = useMemo(() => getCoachActivitiesSortedByLabel(locale), [locale])

  const [selectedActivities, setSelectedActivities] = useState<CoachActivityTypeEnum[]>(() =>
    coach.state.activities.filter((a) => a.kind === "predefined").map((a) => a.type),
  )
  const [otherActivities, setOtherActivities] = useState<string[]>(() =>
    coach.state.activities.filter((a) => a.kind === "other").map((a) => a.value),
  )

  const totalActivities = selectedActivities.length + otherActivities.length
  const canAddActivity = totalActivities < MAX_ACTIVITIES

  const form = useAppForm({
    defaultValues: getCoachFormDataDefaultValues(coach, user),
    validators: {
      onSubmit: coachFormDataSchema(locale),
      onBlur: coachFormDataSchema(locale),
    },
    onSubmit: async ({ value }) => {
      const activityTypes = [
        ...selectedActivities.map((type, index) => ({
          id: coach.state.activities.find((a) => a.kind === "predefined" && a.type === type)?.id ?? null,
          orderIndex: index,
          type,
          otherType: null,
          principal: true,
          creationDate: null,
          archived: false,
          archivedDate: null,
        })),
        ...otherActivities.filter(Boolean).map((v, i) => ({
          id: coach.state.activities.find((a) => a.kind === "other" && a.value === v)?.id ?? null,
          orderIndex: selectedActivities.length + i,
          type: CoachActivityTypeEnum.OTHER,
          otherType: v,
          principal: false,
          creationDate: null,
          archived: false,
          archivedDate: null,
        })),
      ]

      const additionalInfo = [value.other1, value.other2, value.other3].filter(Boolean).map((information, idx) => ({
        id: null,
        orderIndex: idx,
        information,
        creationDate: null,
        archived: false,
        archivedDate: null,
      }))

      try {
        await updateCoachSettings({
          id: coach.state.id,
          payload: {
            role: value.role || null,
            firstname: coach.state.firstname,
            lastname: coach.state.lastname,
            commercialName: value.commercialName || null,
            yearsOfExperience: value.yearsOfExperience ? parseInt(value.yearsOfExperience, 10) : null,
            email: value.email || null,
            phoneNumber:
              value.phoneNumber.number && value.phoneNumber.countryCode
                ? { number: value.phoneNumber.number, countryCode: value.phoneNumber.countryCode }
                : null,
            description: value.description || null,
            city: value.city || null,
            region: value.region || null,
            country: value.country || coach.state.country,
            imageKey: value.profilePhoto?.key ?? null,
            activityTypes,
            training: value.formation || null,
            imageKeys: value.galleryImages.map((img) => img.key ?? "").filter((k) => !!k),
            additionalInformation: additionalInfo,
          },
        })
        toast.success(word("coach.admin.vitrine.save_success"), {
          classNames: successToastClassName(),
        })
      } catch (error: unknown) {
        console.error(LoggerType.Coach + "update", error)
        toast.error(word("global.error.label"), {
          description: word("global.error.support"),
          classNames: errorToastClassName(),
        })
      }
    },
  })

  function addOtherActivity() {
    if (canAddActivity) setOtherActivities((prev) => [...prev, ""])
  }

  function updateOtherActivity(index: number, value: string) {
    setOtherActivities((prev) => prev.map((v, i) => (i === index ? value : v)))
  }

  function removeOtherActivity(index: number) {
    setOtherActivities((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
      className="flex flex-col gap-6 lg:flex-row"
    >
      {/* Left column */}
      <div className="flex min-w-0 flex-1 flex-col gap-8 rounded-xl bg-surface-background-secondary p-6 pb-12">
        <CoachBannerCard />

        <div className="flex flex-col gap-4">
          <CoachSectionCard
            title={word("coach.admin.vitrine.header.section_title")}
            subtitle={word("coach.admin.vitrine.header.section_subtitle")}
          />
          <div className="flex flex-col gap-4 rounded-xl bg-interactive-background-neutral-default p-4">
            <form.AppField name="commercialName">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.commercial_name")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.AppField name="role">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.role_title")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input
                      placeholder={word("coach.admin.vitrine.header.role_placeholder")}
                      maxLength={ROLE_MAX_LENGTH}
                    />
                    <div className="flex justify-end">
                      <Text fontStyle="LS" className="text-textOnSurface-muted">
                        {field.state.value.length}/{ROLE_MAX_LENGTH}
                      </Text>
                    </div>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.AppField name="yearsOfExperience">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.years_experience")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input type="number" placeholder="5" min="0" max="99" step="1" />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.AppField name="city">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.city")}
                    <field.Label fontStyle="LM" className="text-textOnSurface-muted normal-case">
                      {word("coach.admin.vitrine.header.optional")}
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input
                      placeholder={word("coach.admin.vitrine.header.city_placeholder")}
                      maxLength={CITY_MAX_LENGTH}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.AppField name="region">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.region")}
                    <field.Label fontStyle="LM" className="text-textOnSurface-muted normal-case">
                      {word("coach.admin.vitrine.header.optional")}
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input
                      placeholder={word("coach.admin.vitrine.header.region_placeholder")}
                      maxLength={REGION_MAX_LENGTH}
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.AppField name="country">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.header.country")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.CountrySelect />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <div className="flex flex-col gap-2">
              <Text fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                {word("coach.admin.vitrine.header.activity_type")}
                <Text fontStyle="LM" className="text-feedback-negative-intense">
                  *
                </Text>
              </Text>

              <Combobox
                value={selectedActivities}
                onValueChange={(vals) => {
                  if (vals.length <= MAX_ACTIVITIES - otherActivities.length) {
                    setSelectedActivities(vals)
                  }
                }}
                multiple
              >
                <ComboboxChips ref={chipsAnchor}>
                  {selectedActivities.map((activity) => (
                    <ComboboxChip key={activity}>{word(`coach.activity.${activity}`)}</ComboboxChip>
                  ))}
                  <ComboboxChipsInput
                    placeholder={
                      selectedActivities.length === 0
                        ? word("coach.admin.vitrine.header.activity_placeholder")
                        : undefined
                    }
                  />
                </ComboboxChips>
                <ComboboxContent anchor={chipsAnchor}>
                  <ComboboxList>
                    {coachActivitiesSortedByLabel.map((activity) => (
                      <ComboboxItem key={activity} value={activity}>
                        {word(`coach.activity.${activity}`)}
                      </ComboboxItem>
                    ))}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>

              {otherActivities.map((value, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={value}
                    maxLength={FREE_ACTIVITY_MAX_LENGTH}
                    onChange={(e) => {
                      updateOtherActivity(index, e.target.value)
                    }}
                    placeholder={word("coach.admin.vitrine.header.activity_placeholder")}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      removeOtherActivity(index)
                    }}
                    className="shrink-0 cursor-pointer text-textOnSurface-muted hover:text-textOnSurface-default"
                  >
                    ✕
                  </button>
                </div>
              ))}

              {canAddActivity && (
                <Button type="button" variant="secondary" className="w-full gap-2" onClick={addOtherActivity}>
                  <Plus className="size-4" />
                  {word("coach.admin.vitrine.header.add_activity")}
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <CoachSectionCard
            title={word("coach.admin.vitrine.about.section_title")}
            subtitle={word("coach.admin.vitrine.about.section_subtitle")}
          />
          <div className="flex flex-col gap-4 rounded-xl bg-interactive-background-neutral-default p-4">
            <form.AppField name="description">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.about.description")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Textarea
                      placeholder={word("coach.admin.vitrine.about.description_hint")}
                      rows={6}
                      maxLength={DESCRIPTION_MAX_LENGTH}
                    />
                    <div className="flex justify-end">
                      <Text fontStyle="LS" className="text-textOnSurface-muted">
                        {field.state.value.length}/{DESCRIPTION_MAX_LENGTH}
                      </Text>
                    </div>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <div className="grid grid-cols-2 gap-3">
              <form.AppField name="formation">
                {(field) => (
                  <field.Field>
                    <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                      {word("coach.admin.vitrine.about.formation")}
                      <field.Label fontStyle="LM" className="text-textOnSurface-muted normal-case">
                        {word("coach.admin.vitrine.header.optional")}
                      </field.Label>
                    </field.Label>
                    <field.Content>
                      <field.Input placeholder={word("coach.admin.vitrine.about.formation_placeholder")} />
                      <field.Message />
                    </field.Content>
                  </field.Field>
                )}
              </form.AppField>

              {(["other1", "other2", "other3"] as const).map((name) => (
                <form.AppField name={name} key={name}>
                  {(field) => (
                    <field.Field className="self-end">
                      <field.Content>
                        <field.Input placeholder={word(`coach.admin.vitrine.about.${name}`)} />
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <CoachSectionCard title={word("coach.admin.vitrine.contact.section_title")} />
          <div className="flex flex-col rounded-xl bg-interactive-background-neutral-default p-4">
            <form.AppField name="email">
              {(field) => (
                <field.Field>
                  <field.Label fontStyle="LM" className="gap-1 text-textOnSurface-muted uppercase">
                    {word("coach.admin.vitrine.contact.email")}
                    <field.Label fontStyle="LM" className="text-feedback-negative-intense">
                      *
                    </field.Label>
                  </field.Label>
                  <field.Content>
                    <field.Input type="email" />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <CoachSectionCard title={word("coach.admin.vitrine.private.section_title")} />
          <div className="flex flex-col gap-4 rounded-xl bg-interactive-background-neutral-default p-4">
            <form.AppField name="phoneNumber">
              {(field) => (
                <field.Field>
                  <field.Content>
                    <field.PhoneInput
                      label={word("coach.admin.vitrine.private.phone")}
                      labelClassName="text-textOnSurface-muted"
                    />
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>
          </div>
        </div>
      </div>

      {/* Right column */}
      <div className="flex w-full shrink-0 flex-col gap-6 lg:w-64">
        <div className="flex flex-col gap-6 rounded-2xl bg-surface-background-secondary p-6">
          <form.AppField name="profilePhoto">
            {(field) => (
              <field.Field className="gap-4">
                <field.Label fontStyle="LM" className="text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.photo.title")}
                </field.Label>
                <field.Content>
                  <field.ImagePicker
                    type="logo"
                    renderEmpty={
                      <div className="relative flex items-center justify-center">
                        <div className="flex size-38 items-center justify-center rounded-full bg-interactive-background-primary-faded">
                          <Text fontStyle="HL" className="font-semibold text-white">
                            {coach.state.firstname?.[0]?.toUpperCase()}
                            {coach.state.lastname?.[0]?.toUpperCase()}
                          </Text>
                        </div>
                        <Button variant="outline" size="iconMd" className="absolute top-0 right-0">
                          <PlusIcon className="size-5 text-textOnSurface-subtle" />
                        </Button>
                      </div>
                    }
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="galleryImages">
            {(field) => (
              <field.Field className="gap-4">
                <field.Label fontStyle="LM" className="text-textOnSurface-muted uppercase">
                  {word("coach.admin.vitrine.photo.gallery")}
                </field.Label>
                <field.Content>
                  <field.GalleryPicker
                    emptyLabel={word("coach.admin.vitrine.photo.add_image")}
                    addLabel={word("coach.admin.vitrine.photo.add_image")}
                  />
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>
      </div>
    </form>
  )
}
